import React from "react";
import clsx from "clsx";
import "./chat-box-window.styles.scss";

import { Flipper, Flipped } from "react-flip-toolkit";
import { Avatar } from "../Avatar";
import { RippleEffected } from "../RippleEffected";
import { Badge } from "../Badge";

import { ChatBoxWindowContext } from "./context/chat-box-window.context";

import { useElementPosition } from "../../hooks/use-element-position";
import { useContinuousTap } from "../../hooks/use-continuous-tap";
import {
	useChatBoxViewContext,
	useChatBoxListState,
	useChatBoxListUIController,
} from "../../contexts";
import { $Logger } from "../../../logger";

export interface ChatBoxWindowProps {
	windowId: string;
	windowWidth?: number;
	windowHeight?: number;
	windowName?: string;
	initialPosition?: { x: number; y: number };

	className?: string;
	children?: React.ReactNode;
}

const BlockName = "chat-box-window";
const ClassNames = {
	Block: BlockName,
	Modifiers: {
		Normal: `--normal`,
		Minimized: `--minimized`,
	},
	Elements: {
		WindowView: `${BlockName}__window-view`,
		WindowBar: `${BlockName}__window-bar`,
		WindowButtonGroup: `${BlockName}__window-button-group`,
		WindowAvatar: `${BlockName}__window-avatar`,
	},
} as const;

interface WindowAvatarProps {
	windowId: string;
	onMouseDownAvatar: (event: React.MouseEvent<HTMLDivElement>) => void;
	onClickAvatar: (event: React.MouseEvent<HTMLDivElement>) => void;
}
const WindowAvatar = (props: WindowAvatarProps) => {
	const badgeRef = React.useRef<HTMLDivElement>(null);
	const { toUidInfo } = useChatBoxViewContext();
	const { unreadCount } = useChatBoxListState();

	return (
		<Badge
			ref={badgeRef}
			show={true}
			count={unreadCount}
			style={{ opacity: 0 }}
			type={"counter"}
			size={"big"}
			animatable
			animateDirection="down-up"
			position="top-right"
		>
			<Flipped
				flipId={`cbw-avatar-w${props.windowId}`}
				onComplete={() => {
					if (badgeRef.current) badgeRef.current.style.opacity = "1";
				}}
			>
				<RippleEffected>
					<Avatar
						className={ChatBoxWindow.ClassNames.Elements.WindowAvatar}
						size={WINDOW_AVATAR_SIZE}
						alt={toUidInfo.name}
						src={toUidInfo.avatarUrl}
						onMouseDown={props.onMouseDownAvatar}
						onClick={props.onClickAvatar}
					/>
				</RippleEffected>
			</Flipped>
		</Badge>
	);
};

export type ChatBoxWindowComponent = React.FC<ChatBoxWindowProps> & {
	ClassNames: typeof ClassNames;
	Components: {
		WindowAvatar: typeof WindowAvatar;
	};
};

const WINDOW_NAME = "Chat Box Window";
const WINDOW_WIDTH = 560;
const WINDOW_HEIGHT = 720;
const WINDOW_AVATAR_SIZE = 64;

export const ChatBoxWindow = ((props: ChatBoxWindowProps) => {
	const windowName = props.windowName ?? WINDOW_NAME;
	const [windowWidth, setWindowWidth] = React.useState(() => props.windowWidth ?? WINDOW_WIDTH);
	const [windowHeight, setWindowHeight] = React.useState(
		() => props.windowHeight ?? WINDOW_HEIGHT,
	);
	const initialPosition = React.useMemo(() => {
		return {
			x:
				props.initialPosition?.x ??
				(window.innerWidth - (props.windowWidth ?? WINDOW_WIDTH)) / 2,
			y:
				props.initialPosition?.y ??
				(window.innerHeight - (props.windowHeight ?? WINDOW_HEIGHT)) / 2,
		};
	}, [props.initialPosition?.x, props.initialPosition?.y, props.windowWidth, props.windowHeight]);

	const [windowMode, setWindowMode] = React.useState<"normal" | "minimized">("normal");
	const ref = React.useRef<HTMLDivElement | null>(null);
	const { position, setPosition, onMouseDownElement } = useElementPosition({
		initialValue: initialPosition,
	});
	const controller = useChatBoxListUIController();

	const applyWindowMode = (mode: "normal" | "minimized") => {
		if (windowMode === mode) return;
		setWindowMode(mode);
	};

	const handleMinimizeWindowClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const cbvHeaderAvatar = window.document.getElementById(
			`#cbv-header-avatar-w${props.windowId}`,
		);
		if (cbvHeaderAvatar) {
			const cbvHeaderAvatarRect = cbvHeaderAvatar.getBoundingClientRect();
			const newMinimizedPosition: { x: number; y: number } = {
				x: cbvHeaderAvatarRect.x + (cbvHeaderAvatarRect.width - WINDOW_AVATAR_SIZE) / 2,
				y: cbvHeaderAvatarRect.y + (cbvHeaderAvatarRect.height - WINDOW_AVATAR_SIZE) / 2,
			};
			setPosition(newMinimizedPosition);
		}
		const chatBoxWindow = ref.current;
		if (chatBoxWindow) {
			const chatBoxWindowRect = chatBoxWindow.getBoundingClientRect();
			setWindowWidth(chatBoxWindowRect.width);
			setWindowHeight(chatBoxWindowRect.height);
		}
		applyWindowMode("minimized");
	};
	const handleCloseWindowClick = () => {};
	const handleExtendWindowClick = () => {
		applyWindowMode("normal");
	};

	const handleDoubleClick = useContinuousTap((type, event) => {
		if (type === "single") return;

		const windowAvatar = event.currentTarget as HTMLElement;
		if (windowAvatar) {
			const windowAvatarRect = windowAvatar.getBoundingClientRect();
			const newNormalPosition: { x: number; y: number } = {
				x: windowAvatarRect.x + 64 / 2 - 48 / 2 - 16,
				y: windowAvatarRect.y + 64 / 2 - 48 / 2 - 32,
			};
			setPosition(newNormalPosition);
		}
		applyWindowMode("normal");
		controller.clearUnread();
	});

	const onChangeWindowMode = React.useCallback((mode: "normal" | "minimized") => {
		setWindowMode(mode);
	}, []);

	return (
		<Flipper flipKey={windowMode}>
			{windowMode === "normal" && (
				<Flipped
					flipId={`cbw-${props.windowId}`}
					shouldFlip={() => false}
					onExit={(element, _, removeElement, __) => {
						element.style.zIndex = "0";
						element.style.transformOrigin = `40px 58px`;
						const anim = element.animate(
							[
								{
									opacity: 1,
									transform: "scale(1)",
								},
								{ opacity: 0, offset: 0.35 },
								{
									transform: "scale(0.35)",
									opacity: 0,
								},
							],
							{ duration: 600, easing: "ease-out" },
						);
						anim.finished.then(() => {
							anim.cancel();
							removeElement();
						});
					}}
				>
					<div
						ref={ref}
						style={{
							transform: `translate(${position.x}px, ${position.y}px)`,
							width: windowWidth,
							height: windowHeight,
						}}
						className={clsx(
							ChatBoxWindow.ClassNames.Block,
							ChatBoxWindow.ClassNames.Modifiers.Normal,
							props.className,
						)}
					>
						<div className={ChatBoxWindow.ClassNames.Elements.WindowView}>
							<div
								className={ChatBoxWindow.ClassNames.Elements.WindowBar}
								onMouseDown={onMouseDownElement}
							>
								<div className="text-center">{windowName}</div>
								<div
									className={clsx(
										ChatBoxWindow.ClassNames.Elements.WindowButtonGroup,
									)}
								>
									<button className="--closed" onClick={handleCloseWindowClick} />
									<button
										className="--minimized"
										onClick={handleMinimizeWindowClick}
									/>
									<button
										className="--extended"
										onClick={handleExtendWindowClick}
									/>
								</div>
							</div>
							<ChatBoxWindowContext.Provider
								value={{
									width: windowWidth,
									height: windowHeight,
									x: position.x,
									y: position.y,
									id: props.windowId,
									name: windowName,
									mode: windowMode,
									onChangeWindowMode,
								}}
								children={props.children}
							/>
						</div>
					</div>
				</Flipped>
			)}
			{windowMode === "minimized" && (
				<div
					style={{
						transform: `translate(${position.x}px, ${position.y}px)`,
					}}
					className={clsx(
						ChatBoxWindow.ClassNames.Block,
						ChatBoxWindow.ClassNames.Modifiers.Minimized,
						props.className,
					)}
				>
					<ChatBoxWindow.Components.WindowAvatar
						windowId={props.windowId}
						onMouseDownAvatar={onMouseDownElement}
						onClickAvatar={handleDoubleClick}
					/>
				</div>
			)}
		</Flipper>
	);
}) as ChatBoxWindowComponent;
ChatBoxWindow.ClassNames = ClassNames;
ChatBoxWindow.Components = {
	WindowAvatar,
};
